import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import {
    createColumnMenu,
    formatLastUpdatedDate,
    renderlastUpdatedTooltipHeader,
    renderTooltipHeader
} from "../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogContent, DialogBody, Button } from "@fluentui/react-components";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import { getLastUpdatedDateColorWeeks, NameAndNoDealsBadge } from "../../dashboard-util/dashboard-utils";
import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { isFilteredColumn } from "../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import PropTypes from "prop-types";
import { FluentTooltip } from "../../../../components/FluentUI/Fluent-Tooltip";
import { clearDetailStateFromStorage, getFilteredSortedData, getInitialDetailState, getStafferProjectTrackerMemberDetails, handleStafferProjectMemberDetailsTrackerExport, mapPowerProjectDrillDownExportData, PowerCapacity, saveDetailStateToStorage } from "../dashboard-pui-banker/power-banker-time-log/power-banker-helper";
import "../dashboard-pui-banker/power-banker-time-log/power-banker-time-log.scss";
import { FluentAvatar } from "../../../../components/FluentUI/Fluent-Avatar";

const PowerProjectTrackerModal = ({
    show,
    onHide,
    members,
    user
}) => {
    const [memberState, setMemberState] = useState(() => getInitialDetailState(user?.userID, "powerProjectMemberTracker"));
    const item = members.MemberDetails;
    const _juniorBankersExport = useRef(null);
    const filteredData = useMemo(() =>
        getFilteredSortedData(item, memberState.filter, memberState.sort), [item, memberState.filter, memberState.sort]);
    const visibleData = useMemo(() => filteredData, [filteredData]);
    const columnMenu = createColumnMenu(item);
    const isEmpty = visibleData?.length === 0;
    const finalGridData = isEmpty ? [{ isEmptyRow: true, showSearchEmptyState: false, isFilterEmptyState: (memberState.filter?.filters?.length ?? 0) > 0 }] : visibleData;
    const hasAnyFilter =
        memberState.filter &&
        Array.isArray(memberState.filter.filters) &&
        memberState.filter.filters.length > 0;
    const canReset = memberState.sort.length > 0 || hasAnyFilter;
    const columnIsFiltered = useCallback(
        (fieldName) => isFilteredColumn(fieldName, memberState.filter),
        [memberState.filter]
    );

    const resetDataState = () => {
        const resetState = { sort: [], filter: null };
        setMemberState(resetState);
        clearDetailStateFromStorage(user?.userID, "powerProjectMemberTracker");
    };

    useEffect(() => {
        document.body.classList.toggle("energy-dialog-open", show);
    }, [show]);

    return (
        <>
            <Dialog open={show} modalType="modal" data-testid="deal-resource-modal"
                onOpenChange={(event, data) => {
                    if (!data.open) {
                        onHide();
                    }
                }}>
                <DialogSurface aria-describedby={undefined} className="deal-resource-modal">
                    <DialogBody>
                        <DialogTitle className="d-flex justify-content-between "
                            action={
                                <DialogTrigger action="close">
                                    <Button
                                        appearance="subtle"
                                        aria-label="close"
                                        icon={<FluentUiIcon name="DismissRegular" />}
                                    />
                                </DialogTrigger>
                            }
                        >
                            <span>{members?.ProjectName} • {members?.ClientName}
                                <p className="subDepartmentTitle">Project Tracker</p>
                            </span>
                            <div className="d-flex align-items-start justify-content-end">
                                <div className="mr-2">
                                <FluentTooltip content="Reset Filters and Sorting"
                                        target={
                                            <FluentButton
                                            
                                                appearance="secondaryWithOutline"
                                                icon={
                                                    <FluentUiIcon
                                                        name="ArrowResetRegular"
                                                        color={canReset ? "#0F6CBD" : "#A0A0A0"}
                                                        size={20}
                                                        disabled={!canReset}
                                                    />
                                                }
                                                onClick={resetDataState}
                                            />
                                        }
                                >
                                </FluentTooltip>
                                </div>
                                <FluentTooltip content="Export Data" target={
                                    <FluentButton
                                        appearance="secondaryWithOutline"
                                        icon={
                                            <FluentUiIcon
                                                name="ArrowDownloadRegular"
                                                color="#0F6CBD"
                                                size={20}
                                            />
                                        }
                                        onClick={() =>
                                            handleStafferProjectMemberDetailsTrackerExport({ jBRef: _juniorBankersExport, ProjectName: members?.ProjectName, ClientName: members?.ClientName })
                                        }
                                    />
                                }>
                                </FluentTooltip>
                            </div>
                        </DialogTitle>

                        <DialogContent>
                            <div className="detail-grid-scroll-container ">
                                <Grid
                                    data={finalGridData}
                                    dataItemKey={"Banker"}
                                    sortable={true}
                                    sort={memberState.sort}
                                    filter={memberState.filter}
                                    onSortChange={(e) => {
                                        setMemberState({ ...memberState, sort: e.sort })
                                        saveDetailStateToStorage(user.userID, { ...memberState, sort: e.sort }, "", "powerProjectMemberTracker");
                                    }}
                                    onFilterChange={(e) => {
                                        saveDetailStateToStorage(user.userID, { ...memberState, filter: e.filter }, "", "powerProjectMemberTracker");
                                        setMemberState({ ...memberState, filter: e.filter })
                                    }}
                                    resizable={true}
                                >

                                    <Column
                                        field="Banker"
                                        title={renderTooltipHeader("Analyst/Associate")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("Banker") ? "filtered" : ""}
                                        cell={(props) => (
                                            <td className="background-white k-table-td ">
                                                <div className="dashboard-username">
                                                    <FluentAvatar size={28} name={props.dataItem.Banker} className="task-member-user mr-2" />
                                                    <NameAndNoDealsBadge name={props.dataItem.Banker ?? '—'} />
                                                </div>
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="OverallCapacity"
                                        title={renderTooltipHeader("Overall Capacity")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("OverallCapacity") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="summary-cell k-table-td" >
                                                {props.dataItem.OverallCapacity ? <PowerCapacity capacity={props.dataItem.OverallCapacity} /> : "—"}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="LastWeekHrs"
                                        title={renderTooltipHeader("Hrs Spent Last Wk")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("LastWeekHrs") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="summary-cell k-table-td" >
                                                {props.dataItem.LastWeekHrs ?? "—"}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="WeekendHours"
                                        title={renderTooltipHeader("Wknd Hrs Spent")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("WeekendHours") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="summary-cell k-table-td" >
                                                {props.dataItem.WeekendHours ?? "—"}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="TotalHrs"
                                        title={renderTooltipHeader("Total Hrs")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("TotalHrs") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="summary-cell k-table-td" >
                                                {props.dataItem.TotalHrs ?? "—"}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="EstHoursForNextWeek"
                                        title={renderTooltipHeader("Est. Hrs for Nxt Wk")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("EstHoursForNextWeek") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="summary-cell k-table-td" >
                                                {props.dataItem.EstHoursForNextWeek ?? "—"}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="LastUpdated"
                                        title={renderlastUpdatedTooltipHeader("Last Updated")}
                                        columnMenu={columnMenu}
                                        filter="date"
                                        headerClassName={columnIsFiltered("LastUpdated") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="background-white k-table-td" style={{ color: getLastUpdatedDateColorWeeks(props.dataItem.LastUpdated) }}>
                                                <span>{formatLastUpdatedDate(props.dataItem.LastUpdated)}</span>
                                            </td>
                                        )}
                                    />
                                </Grid>
                            </div>
                        </DialogContent>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            <ExcelExport data={mapPowerProjectDrillDownExportData(item, members.ClientName, members.ProjectName, members.ProjectId)} ref={_juniorBankersExport}>
                {getStafferProjectTrackerMemberDetails()}
            </ExcelExport>
        </>
    );
};

PowerProjectTrackerModal.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    members: PropTypes.object,
    user: PropTypes.object,
};

export default PowerProjectTrackerModal;
